import { feedbackcard, YesObject } from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { FieldSettings } from 'shared/types'
import { downloadUrl } from 'shared/utils'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import { RelationConfigProps } from 'site/Renderer/components/ObjectView/components/types/object-view'
import useReportData from '../useReportData/useReportData'
import useReportPdfs from '../useReportPdfs/useReportPdfs'
import useReportZip from '../useReportZip/useReportZip'
import useSplitReportComponents from '../useSplitReportComponents/useSplitReportComponents'

type UsePDFProcessingProps = {
  enabled: boolean
  reportType: 'list' | 'card'
  downloadType: 'single-pdf' | 'multiple-pdf-zip'
  splitThreshold: number
  yesObject?: YesObject
  layoutClassificationUuid?: string
  filteractionUuid?: string
  relationConfig?: RelationConfigProps
  fieldSettings?: FieldSettings
  concatFields?: ConcatFields
  classificationUuid?: string
}

const usePDFProcessing = ({
  enabled,
  reportType,
  downloadType,
  yesObject,
  layoutClassificationUuid,
  filteractionUuid,
  splitThreshold,
  relationConfig,
  fieldSettings,
  concatFields,
  classificationUuid
}: UsePDFProcessingProps) => {
  const {
    objectFields,
    fieldValues,
    filterComposition,
    filterFields,
    yesObjects: filterYesObjects,
    requestLoading,
    requestFetched,
    relationsTableLayouts,
    filteractionName,
    error: dataError
  } = useReportData({
    filteractionUuid,
    relationConfig,
    fieldSettings,
    classificationUuid,
    enabled,
    reportType
  })

  const yesObjects = yesObject ? [yesObject] : filterYesObjects

  const reportComponents = useSplitReportComponents({
    ...objectFields,
    fieldValues,
    filterComposition,
    filterFields,
    reportType,
    layoutClassificationUuid,
    splitThreshold,
    yesObjects,
    filteractionName,
    relationsTableLayouts,
    concatFields,
    relationConfig,
    fieldSettings,
    showHeaderForEveryChunk: downloadType === 'multiple-pdf-zip',
    isSinglePdfExport: downloadType === 'single-pdf' && splitThreshold === 1,
    enabled: enabled && requestFetched && yesObjects.length > 0
  })

  const shouldMerge = downloadType !== 'multiple-pdf-zip'

  const pdfsEnabled = reportComponents.length > 0

  const {
    urls,
    blobs,
    isRendering,
    isRenderFinished,
    error: pdfError
  } = useReportPdfs({
    components: reportComponents,
    sourceObjects: yesObjects,
    shouldMerge,
    enabled: pdfsEnabled
  })

  const zipEnabled =
    enabled && downloadType === 'multiple-pdf-zip' && blobs.length > 0

  const {
    url: zipUrl,
    isPacking,
    isPackingFinished,
    error: zipError
  } = useReportZip({
    blobs,
    sourceObjects: yesObjects,
    enabled: zipEnabled
  })

  const currentError = dataError || pdfError || zipError

  const calculatedUrl = zipEnabled
    ? zipUrl
    : shouldMerge && urls.length > 0
      ? urls[0]
      : null

  const isLoading = requestLoading || isRendering || isPacking

  const allLoadsFinished =
    requestFetched &&
    isRenderFinished &&
    (isPackingFinished || !zipEnabled) &&
    !isLoading

  return {
    currentError,
    calculatedUrl,
    isLoading,
    allLoadsFinished
  }
}

type UseExportPDFProps = {
  reportType: 'list' | 'card'
  downloadType: 'single-pdf' | 'multiple-pdf-zip'
  yesObject?: YesObject
  layoutClassificationUuid?: string
  filteractionUuid?: string
  splitThreshold?: number
  relationConfig?: RelationConfigProps
  fieldSettings?: FieldSettings
  concatFields?: ConcatFields
  classificationUuid?: string
}

type UseExportPDFReturn = {
  exportPDFReport: () => void
  isLoading: boolean
  error: Error | null
}

const useExportPDF = ({
  splitThreshold = 10,
  layoutClassificationUuid,
  filteractionUuid,
  reportType,
  downloadType,
  yesObject,
  relationConfig,
  fieldSettings,
  concatFields,
  classificationUuid
}: UseExportPDFProps): UseExportPDFReturn => {
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [finalUrl, setFinalUrl] = useState<string | null>(null)

  const { currentError, isLoading, allLoadsFinished, calculatedUrl } =
    usePDFProcessing({
      enabled: isExporting,
      splitThreshold,
      layoutClassificationUuid,
      filteractionUuid,
      reportType,
      downloadType,
      yesObject,
      relationConfig,
      fieldSettings,
      concatFields,
      classificationUuid
    })

  useEffect(() => {
    if (!isExporting) return

    if (currentError) {
      setIsExporting(false) // stop exporting on any error
      setError(currentError)
      setFinalUrl(null)
      return
    }

    if (!allLoadsFinished) {
      return
    }

    const noDataError = new Error(
      'Unknown error generating report. Data is missing'
    )

    setIsExporting(false)
    setError(calculatedUrl ? null : noDataError)
    setFinalUrl(calculatedUrl)
  }, [allLoadsFinished, calculatedUrl, currentError, isExporting])

  const { t } = useTranslation()

  const filename = downloadType === 'single-pdf' ? 'report.pdf' : 'report.zip'

  useEffect(() => {
    if (finalUrl) {
      downloadUrl({
        url: finalUrl,
        filename
      })

      feedbackcard(t('report.download-finished-title'), {
        message: t('report.download-finished-message'),
        type: 'success'
      })
    }
  }, [filename, finalUrl, t])

  useEffect(() => {
    if (error) {
      console.error({ error })

      feedbackcard(t('report.download-error-title'), {
        message: t('report.download-error-message'),
        type: 'error'
      })
    }
  }, [error, t])

  const exportPDFReport = useCallback(() => {
    setError(null)
    setFinalUrl(null)
    setIsExporting(true)
  }, [])

  return {
    exportPDFReport,
    isLoading,
    error
  }
}

export default useExportPDF
